import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Question } from '../../models/underwriter.model';
import { newGuid } from '../question-helper';

declare var $: any;

export interface CsvUploadColumn {
  key: string;
  label: string;
  required?: boolean;
}

export interface CsvUploadReviewRow {
  values: string[];
  errors: boolean[];
  selected: boolean;
  added?: boolean;
}

@Component({
    selector: 'app-question-csv-upload',
    templateUrl: './question-csv-upload.component.html',
    styleUrls: ['./question-csv-upload.component.scss'],
    standalone: false
})
export class QuestionCsvUploadComponent implements OnInit, OnChanges {

  @Input() question: Question;
  @Input() readOnly = false;
  @Output() questionAnswer = new EventEmitter<Question>();

  uniqueId = `question-csv-upload-${newGuid()}`;

  // Target fields the CSV columns get mapped onto.
  // Configurable per question via question.options as a JSON array of
  // { key, label, required } objects (or an array of label strings).
  columns: CsvUploadColumn[] = [
    { key: 'name', label: 'Name', required: true },
    { key: 'surname', label: 'Surname', required: true },
    { key: 'contact_number', label: 'Contact number', required: true },
    { key: 'email', label: 'Email address', required: true },
    { key: 'id_number', label: 'ID number', required: true },
    { key: 'imei', label: 'IMEI number', required: true }
  ];

  // Parsed CSV
  csvHeaders: string[] = [];
  csvRows: string[][] = [];
  fileName = '';
  fileError = '';

  // Map columns step: for each CSV column, the index of the target column (-1 = ignore)
  columnMapping: number[] = [];

  // Review step
  reviewRows: CsvUploadReviewRow[] = [];
  searchTerm = '';
  page = 1;
  pageSize = 10;
  showValidationError = false;

  importedCount = 0;

  constructor() {}

  ngOnInit(): void {
    this.setColumnsFromQuestion();
    this.rehydrate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.question?.currentValue !== changes?.question?.previousValue) {
      this.question = changes.question.currentValue;
      this.setColumnsFromQuestion();
      this.rehydrate();
    }
  }

  private setColumnsFromQuestion(): void {
    try {
      const options = JSON.parse(this.question?.options);
      if (Array.isArray(options) && options.length) {
        this.columns = options.map((option: any) => typeof option === 'string'
          ? { key: option.toLowerCase().replace(/[^a-z0-9]+/g, '_'), label: option, required: true }
          : { key: option?.key, label: option?.label, required: option?.required !== false });
      }
    } catch (e) {
      // no / invalid options - keep defaults
    }
  }

  private rehydrate(): void {
    this.importedCount = 0;
    if (!this.question?.answer) {
      return;
    }
    try {
      const saved = JSON.parse(this.question.answer);
      if (Array.isArray(saved?.rows)) {
        this.reviewRows = saved.rows.map((row: any) => ({
          values: this.columns.map(column => row?.[column.key] ?? ''),
          errors: this.columns.map(() => false),
          selected: false
        }));
        this.importedCount = this.reviewRows.length;
      }
    } catch (e) {
      // answer is not csv data - ignore
    }
  }

  //
  // Step 1 - template download
  //
  downloadTemplate(): void {
    const csv = this.columns.map(column => this.escapeCsvValue(column.label)).join(',') + '\r\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'upload-template.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }

  //
  // Step 2 - upload + parse
  //
  openUploadModal(): void {
    this.fileError = '';
    $(`#${this.uniqueId}-upload`).modal('show');
  }

  onFileSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) {
      return;
    }
    this.fileError = '';
    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = this.parseCsv(reader.result as string);
      if (!parsed.length || !parsed[0].length) {
        this.fileError = 'We could not read any data from that file. Please check it and try again.';
        this.fileName = '';
        return;
      }
      this.csvHeaders = parsed[0];
      this.csvRows = parsed.slice(1).filter(row => row.some(cell => cell?.trim() !== ''));
      this.autoMapColumns();
    };
    reader.readAsText(file);
    event.target.value = '';
  }

  submitUpload(): void {
    if (!this.csvHeaders.length) {
      this.fileError = 'Please choose a CSV file first.';
      return;
    }
    $(`#${this.uniqueId}-upload`).modal('hide');
    $(`#${this.uniqueId}-map`).modal('show');
  }

  //
  // Step 3 - map columns
  //
  private autoMapColumns(): void {
    const normalize = (value: string) => (value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    this.columnMapping = this.csvHeaders.map(header => {
      return this.columns.findIndex(column =>
        normalize(column.label) === normalize(header) || normalize(column.key) === normalize(header));
    });
  }

  get mappingPreviewRows(): string[][] {
    return this.csvRows.slice(0, 10);
  }

  isColumnMapped(targetIndex: number): boolean {
    return this.columnMapping.indexOf(targetIndex) !== -1;
  }

  get unmappedRequiredColumns(): CsvUploadColumn[] {
    return this.columns.filter((column, index) => column.required && !this.isColumnMapped(index));
  }

  confirmMapping(): void {
    this.reviewRows = this.csvRows.map(csvRow => {
      const values = this.columns.map(() => '');
      this.columnMapping.forEach((targetIndex, csvIndex) => {
        if (targetIndex > -1) {
          values[targetIndex] = (csvRow[csvIndex] ?? '').trim();
        }
      });
      return { values, errors: this.columns.map(() => false), selected: false };
    });
    this.searchTerm = '';
    this.page = 1;
    this.showValidationError = false;
    $(`#${this.uniqueId}-map`).modal('hide');
    $(`#${this.uniqueId}-review`).modal('show');
  }

  backToMapping(): void {
    $(`#${this.uniqueId}-review`).modal('hide');
    $(`#${this.uniqueId}-map`).modal('show');
  }

  //
  // Step 4 - review and edit
  //
  get filteredRows(): CsvUploadReviewRow[] {
    const term = this.searchTerm?.trim().toLowerCase();
    if (!term) {
      return this.reviewRows;
    }
    return this.reviewRows.filter(row => row.values.some(value => value?.toLowerCase().includes(term)));
  }

  get pagedRows(): CsvUploadReviewRow[] {
    return this.filteredRows.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
  }

  get selectedCount(): number {
    return this.reviewRows.filter(row => row.selected).length;
  }

  onSearchChanged(): void {
    this.page = 1;
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.page = page;
    }
  }

  addRow(): void {
    const row: CsvUploadReviewRow = {
      values: this.columns.map(() => ''),
      errors: this.columns.map(() => false),
      selected: false,
      added: true
    };
    this.reviewRows.push(row);
    this.searchTerm = '';
    this.page = this.totalPages;
    setTimeout(() => row.added = false, 1000);
  }

  deleteRow(row: CsvUploadReviewRow): void {
    this.reviewRows = this.reviewRows.filter(item => item !== row);
    this.page = Math.min(this.page, this.totalPages);
  }

  cancelSelection(): void {
    this.reviewRows.forEach(row => row.selected = false);
  }

  deleteSelection(): void {
    this.reviewRows = this.reviewRows.filter(row => !row.selected);
    this.page = Math.min(this.page, this.totalPages);
  }

  onCellChanged(row: CsvUploadReviewRow, index: number): void {
    if (row.errors[index]) {
      row.errors[index] = false;
      this.showValidationError = this.reviewRows.some(item => item.errors.some(error => error));
    }
  }

  exportCsv(): void {
    const lines = [this.columns.map(column => this.escapeCsvValue(column.label)).join(',')];
    this.reviewRows.forEach(row => lines.push(row.values.map(value => this.escapeCsvValue(value)).join(',')));
    const blob = new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = this.fileName || 'export.csv';
    link.click();
    URL.revokeObjectURL(link.href);
  }

  saveImport(): void {
    let hasErrors = false;
    this.reviewRows.forEach(row => {
      row.errors = this.columns.map((column, index) => {
        const invalid = !!column.required && !row.values[index]?.trim();
        hasErrors = hasErrors || invalid;
        return invalid;
      });
    });
    this.showValidationError = hasErrors;
    if (hasErrors) {
      this.cancelSelection();
      const firstErrorIndex = this.reviewRows.findIndex(row => row.errors.some(error => error));
      this.searchTerm = '';
      this.page = Math.floor(firstErrorIndex / this.pageSize) + 1;
      return;
    }

    const rows = this.reviewRows.map(row => {
      const record: any = {};
      this.columns.forEach((column, index) => record[column.key] = row.values[index]?.trim() ?? '');
      return record;
    });
    this.importedCount = rows.length;
    this.question.answer = JSON.stringify({
      columns: this.columns.map(column => column.key),
      rows
    });
    this.questionAnswer.emit(this.question);
    $(`#${this.uniqueId}-review`).modal('hide');
  }

  //
  // CSV helpers
  //
  private parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let value = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (inQuotes) {
        if (char === '"') {
          if (text[i + 1] === '"') {
            value += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          value += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        row.push(value);
        value = '';
      } else if (char === '\n' || char === '\r') {
        if (char === '\r' && text[i + 1] === '\n') {
          i++;
        }
        row.push(value);
        value = '';
        rows.push(row);
        row = [];
      } else {
        value += char;
      }
    }
    if (value !== '' || row.length) {
      row.push(value);
      rows.push(row);
    }
    return rows;
  }

  private escapeCsvValue(value: string): string {
    const safe = value ?? '';
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
  }

}
